import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Gamepad2, Sparkles } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  GameStatsStrip,
  SubjectTile,
  type MyGamesResponse,
  type MyGamesStatsResponse,
} from '../components/games';

export const Route = createFileRoute('/_authed/games')({
  component: GamesIndexPage,
});

function GamesIndexPage() {
  const role = useAuthStore((s) => s.user?.primaryRole);

  // Backend gates `/api/me/games*` behind `module_games` AND
  // `module_assessment_engine`. Either off → 404 `feature_disabled`,
  // which `GatedSection` turns into the "Coming soon" card. We also
  // softly guard the role: the API will return 403 for non-students,
  // but a friendly empty state is nicer than a generic error.
  const gamesQuery = useQuery({
    queryKey: ['games', 'mine'] as const,
    queryFn: ({ signal }) =>
      apiRequest<MyGamesResponse>('/api/me/games', { signal }),
    enabled: role === 'student',
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      if (err instanceof ApiError && err.status === 403) return false;
      return failureCount < 1;
    },
  });

  const statsQuery = useQuery({
    queryKey: ['games', 'stats'] as const,
    queryFn: ({ signal }) =>
      apiRequest<MyGamesStatsResponse>('/api/me/games/stats', { signal }),
    enabled: role === 'student',
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      if (err instanceof ApiError && err.status === 403) return false;
      return failureCount < 1;
    },
  });

  if (role !== 'student') {
    return (
      <ListOrOutlet basePath="/games">
      <ListShell title="Games">
        <EmptyState
          title="Games are for students"
          description="Switch to a student account to see this week's quizzes."
          icon={<Gamepad2 size={32} aria-hidden="true" />}
          action={
            <Link to="/dashboard">
              <Button variant="secondary">Back to dashboard</Button>
            </Link>
          }
        />
      </ListShell>
      </ListOrOutlet>
    );
  }

  // Either query's `feature_disabled` (404) will trigger the gated card; the
  // games query is the canary because it's the screen's primary data.
  return (
    <ListOrOutlet basePath="/games">
    <ListShell
      title="Games"
      description="Beat this week's quiz, build your streak, and share the love."
    >
      {/* Subtle warm ambient panel — gives the page its game-like personality
          without a heavy hero. */}
      <div className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-6 right-0 h-40 w-72 rounded-full bg-gold-500/10 blur-3xl"
        />
        <GatedSection
          error={gamesQuery.error ?? statsQuery.error}
          title="Games"
          description="Weekly quizzes, streaks, and bragging rights — coming as soon as the academy switches the Games module on."
          onRetry={() => {
            void gamesQuery.refetch();
            void statsQuery.refetch();
          }}
        >
          <div className="space-y-6">
            <GameStatsStrip
              stats={statsQuery.data}
              isLoading={statsQuery.isLoading}
            />

            <section aria-labelledby="games-open-heading" className="space-y-3">
              <header className="flex items-center justify-between gap-3">
                <h2
                  id="games-open-heading"
                  className="font-display text-lg font-semibold text-[color:var(--color-text)]"
                >
                  Open quizzes
                </h2>
                {gamesQuery.data?.items?.length ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-surface)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--color-text-muted)]">
                    <Sparkles size={12} aria-hidden="true" className="text-gold-600" />
                    {gamesQuery.data.items.length}{' '}
                    {gamesQuery.data.items.length === 1 ? 'quiz' : 'quizzes'} for you
                  </span>
                ) : null}
              </header>

              {gamesQuery.isLoading ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <Skeleton className="h-48 w-full rounded-2xl" />
                  <Skeleton className="h-48 w-full rounded-2xl" />
                </div>
              ) : (gamesQuery.data?.items ?? []).length === 0 ? (
                <EmptyState
                  title="No quizzes open right now"
                  description="Check back later in the week — your teacher will publish the next one soon."
                  icon={<Gamepad2 size={32} aria-hidden="true" />}
                  className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]"
                />
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {(gamesQuery.data?.items ?? []).map((item) => (
                    <SubjectTile key={item.runId} item={item} />
                  ))}
                </div>
              )}
            </section>
          </div>
        </GatedSection>
      </div>
    </ListShell>
    </ListOrOutlet>
  );
}
